import React from "react";
import { ShieldCheck, Clock, LineChart, Wallet } from "lucide-react";

const PILLARS = [
  {
    Icon: ShieldCheck,
    title: "CRA Compliance, Built In",
    desc: "Workflows aligned to Canadian tax rules to keep you out of trouble and out of penalty zones.",
  },
  {
    Icon: Clock,
    title: "Strict Monthly Deadlines",
    desc: "Reports delivered by the 15th of every month. No backlog. No excuses. No surprises.",
  },
  {
    Icon: LineChart,
    title: "Decision-Ready Reports",
    desc: "Clean P&Ls, balance sheets, and cash flow visibility that actually drive smarter business decisions.",
  },
  {
    Icon: Wallet,
    title: "Predictable Fixed Pricing",
    desc: "Monthly fees you can budget around. No surprise hourly bills, no scope creep, no nickel-and-diming.",
  },
];

export default function WhyUs() {
  return (
    <section className="py-24 md:py-32 bg-navy text-cream relative noise-bg" data-testid="whyus-section">
      <div className="px-6 md:px-12 lg:px-20 max-w-7xl mx-auto relative">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-6">
            <div className="uppercase text-xs tracking-[0.25em] font-bold text-gold mb-4">
              Why DSO Books?
            </div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              You&rsquo;re not paying for bookkeeping. <br />
              <span className="italic text-gold/90">You&rsquo;re buying peace of mind.</span>
            </h2>
          </div>
          <p className="md:col-span-6 text-cream/80 text-base md:text-lg leading-relaxed md:pt-6">
            Built specifically for Canadian contractors, trades, and service businesses
            scaling beyond DIY bookkeeping. We don&rsquo;t just record numbers — we
            deliver financial clarity, compliance confidence, and the discipline of a
            real finance function.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cream/15">
          {PILLARS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`whyus-pillar-${i}`}
              className="bg-navy p-8 group hover:bg-navy2 transition-colors"
            >
              <p.Icon className="h-7 w-7 text-gold mb-6" />
              <h3 className="font-serif text-2xl mb-3 leading-tight">{p.title}</h3>
              <p className="text-cream/70 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid sm:grid-cols-3 gap-6 text-center">
          <Stat label="Reports delivered by" value="15th" suffix="every month" />
          <Stat label="Response time within" value="24h" suffix="guaranteed" />
          <Stat label="Missed CRA deadlines" value="0" suffix="zero tolerance" />
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, suffix }) {
  return (
    <div className="py-6 border-t border-cream/15" data-testid={`stat-${value}`}>
      <div className="text-xs uppercase tracking-[0.25em] text-cream/55 mb-2">{label}</div>
      <div className="font-serif text-5xl text-gold">{value}</div>
      <div className="text-sm text-cream/70 mt-1">{suffix}</div>
    </div>
  );
}
